import React from 'react';
import { 
  Container, 
  Paper, 
  Typography, 
  Divider, 
  Box, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText,
  Link 
} from '@mui/material';
import CircleIcon from '@mui/icons-material/Circle';
import EmailIcon from '@mui/icons-material/Email';
import LanguageIcon from '@mui/icons-material/Language';

const PrivacyInfo = () => {
  // लिस्ट मधील बुलेट्ससाठी छोटा आयकॉन
  const bulletIcon = (
    <ListItemIcon sx={{ minWidth: 25, mt: 0.8 }}>
      <CircleIcon sx={{ fontSize: 8, color: '#1a237e' }} />
    </ListItemIcon>
  );

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Paper elevation={3} sx={{ p: { xs: 3, md: 6 }, borderRadius: 2, borderTop: '6px solid #1a237e' }}>
        
        {/* Header - Center for better look */}
        <Box textAlign="center" mb={5}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1a237e', mb: 1 }}>
            Privacy Policy
          </Typography>
          <Divider sx={{ width: '60px', height: '3px', bgcolor: '#1a237e', mx: 'auto', mb: 2 }} />
        </Box>

        {/* Introduction - Left Aligned */}
        <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.7, textAlign: 'left' }}>
          <strong>Swarsagaramrut</strong> respects your privacy. This Privacy Policy explains how our app collects, uses, and protects user information.
        </Typography>

        {/* 1. Information We Collect */}
        <Box mb={4} sx={{ textAlign: 'left' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a237e', mb: 1.5 }}>
            Information We Collect
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Our app may collect or access the following information based on features you use:
          </Typography>
          <List disablePadding>
            <ListItem sx={{ alignItems: 'flex-start', py: 0.5 }}>{bulletIcon}<ListItemText primary="Camera access (for capturing photos/videos, if applicable)" /></ListItem>
            <ListItem sx={{ alignItems: 'flex-start', py: 0.5 }}>{bulletIcon}<ListItemText primary="Location access (for location-based features, if applicable)" /></ListItem>
            <ListItem sx={{ alignItems: 'flex-start', py: 0.5 }}>{bulletIcon}<ListItemText primary="Media/Storage access (for selecting or uploading images/files, if applicable)" /></ListItem>
            <ListItem sx={{ alignItems: 'flex-start', py: 0.5 }}>{bulletIcon}<ListItemText primary="Basic device information such as device type, OS version, and app diagnostics" /></ListItem>
          </List>
        </Box>

        {/* 2. How We Use Information */}
        <Box mb={4} sx={{ textAlign: 'left' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a237e', mb: 1.5 }}>
            How We Use Information
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>We use information only to:</Typography>
          <List disablePadding>
            <ListItem sx={{ py: 0.5 }}>{bulletIcon}<ListItemText primary="Provide and improve app features" /></ListItem>
            <ListItem sx={{ py: 0.5 }}>{bulletIcon}<ListItemText primary="Enable app functionality requested by users" /></ListItem>
            <ListItem sx={{ py: 0.5 }}>{bulletIcon}<ListItemText primary="Support performance, security, and troubleshooting" /></ListItem>
            <ListItem sx={{ py: 0.5 }}>{bulletIcon}<ListItemText primary="Deliver location-based services (if applicable)" /></ListItem>
          </List>
        </Box>

        {/* 3. Data Sharing */}
        <Box mb={4} sx={{ textAlign: 'left' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a237e', mb: 1.5 }}>
            Data Sharing
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>We do not sell personal data. User data may be shared only:</Typography>
          <List disablePadding>
            <ListItem sx={{ py: 0.5 }}>{bulletIcon}<ListItemText primary="With service providers needed to operate app features" /></ListItem>
            <ListItem sx={{ py: 0.5 }}>{bulletIcon}<ListItemText primary="When required by law" /></ListItem>
            <ListItem sx={{ py: 0.5 }}>{bulletIcon}<ListItemText primary="With user consent" /></ListItem>
          </List>
        </Box>

        {/* 4. Data Security & Retention */}
        <Box mb={4} sx={{ textAlign: 'left' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a237e', mb: 1.5 }}>
            Data Security & Retention
          </Typography>
          <Typography variant="body2" paragraph>
            We take reasonable measures to protect user information from unauthorized access, misuse, or disclosure.
          </Typography>
          <Typography variant="body2">
            We retain data only as long as necessary to provide app functionality or comply with legal requirements.
          </Typography>
        </Box>

        {/* 5. User Rights */}
        <Box mb={4} sx={{ textAlign: 'left' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a237e', mb: 1.5 }}>
            User Rights
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>Users may request:</Typography>
          <List disablePadding sx={{ mb: 2 }}>
            <ListItem sx={{ py: 0.5 }}>{bulletIcon}<ListItemText primary="Access to their data" /></ListItem>
            <ListItem sx={{ py: 0.5 }}>{bulletIcon}<ListItemText primary="Correction of data" /></ListItem>
            <ListItem sx={{ py: 0.5 }}>{bulletIcon}<ListItemText primary="Deletion of data" /></ListItem>
          </List>
          <Typography variant="body2">
            For data deletion requests, contact us at: <strong>swarsagaramrutparivar@gmail.com</strong>
          </Typography>
        </Box>

        {/* 6. Permissions Used */}
        <Box mb={4} sx={{ textAlign: 'left' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a237e', mb: 1.5 }}>
            Permissions Used
          </Typography>
          <List disablePadding>
            <ListItem sx={{ py: 0.5 }}>{bulletIcon}<ListItemText primary="Camera permission for capturing images/content" /></ListItem>
            <ListItem sx={{ py: 0.5 }}>{bulletIcon}<ListItemText primary="Location permission for location-based features" /></ListItem>
            <ListItem sx={{ py: 0.5 }}>{bulletIcon}<ListItemText primary="Media/Storage permission for selecting files or images" /></ListItem>
          </List>
        </Box>

        {/* 7. Children's Privacy & Changes */}
        <Box mb={4} sx={{ textAlign: 'left' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a237e', mb: 1 }}>
            Children's Privacy
          </Typography>
          <Typography variant="body2" paragraph>
            This app is not intended for children under 13 and does not knowingly collect data from children.
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a237e', mb: 1 }}>
            Changes to This Policy
          </Typography>
          <Typography variant="body2">
            We may update this Privacy Policy from time to time. Changes will be posted on this page.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Contact Us - Professional Footer */}
        <Box sx={{ textAlign: 'left' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#1a237e' }}>
            Contact Us
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <EmailIcon sx={{ fontSize: 20, mr: 1.5, color: '#1a237e' }} />
            <Typography variant="body2">
              <Link href="mailto:swarsagaramrutparivar@gmail.com" sx={{ color: 'inherit', textDecoration: 'none' }}>
                swarsagaramrutparivar@gmail.com
              </Link>
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LanguageIcon sx={{ fontSize: 20, mr: 1.5, color: '#1a237e' }} />
            <Typography variant="body2">
              <Link href="https://swarsagaramrut.com" target="_blank" sx={{ color: 'inherit', textDecoration: 'none' }}>
                https://swarsagaramrut.com
              </Link>
            </Typography>
          </Box>
        </Box>

        <Typography variant="caption" display="block" sx={{ mt: 5, textAlign: 'center', color: '#999' }}>
          © {new Date().getFullYear()} Swarsagaramrut. All Rights Reserved.
        </Typography>

      </Paper>
    </Container>
  );
};

export default PrivacyInfo;